import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import useForm, { FieldValues } from 'hooks/useForm';
import TextInput from 'components/common/TextInput/TextInput';
import { isPercentage, isValidEmail, isValidPhone, validateRequiredField } from 'common/utils/validation.utils';
import InputSelect from 'components/common/InputSelect/InputSelect';
import PhoneNumberInput from 'components/common/PhoneNumberInput/PhoneNumberInput';
import Button from 'components/common/Button/Button';
import { removeNonDigits } from 'common/utils/string.utils';
import { RootStoreContext } from 'index';
import { OwnerInformation } from 'components/prequalification/prequalification.dto';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { notify } from 'components/common/Toast/toast.helpers';
import { ApplicationRoute } from 'routes/routes.enums';
import { DeleteIcon } from 'components/common/Icons/Icons';

import styles from '../RegistrationProcess.module.scss';
import {
  DefaultYesNoQuestion,
  InputLabel,
  OwnersFormInput,
  PrequalificationEntrySource,
  PrequalificationStep,
  PrequalificationStepType,
} from '../registrationProcess.enums';
import {
  DEFAULT_YES_NO_QUESTION_OPTIONS,
  HOW_MANY_OWNERS_OPTIONS,
  CONFLICT_ERROR_STATUS_CODE,
  OWNER_POSITION_OPTIONS,
  TOTAL_PERCENTAGE_OWNED_MAX_VALUE,
  OWNERS_FORM_DATA_SESSION_STORAGE_KEY,
  EXISTING_USER_ERROR_MESSAGE,
} from '../registrationProcess.constants';
import {
  convertToBooleanFromDefaultQuestionValue,
  convertToDefaultQuestionValueFromBoolean,
  validatePercentageOwned,
} from '../registrationProcess.utils';
import { DISABLED_OWNER_EMAIL_TOOLTIP_TITLE } from './registrationSteps.constants';

export interface OwnerSection {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  isSalesperson: string;
  phone: string;
  email: string;
  percentageOwned: string;
  isMainContact: string;
}

const OwnersInformation = () => {
  const navigator = useNavigate();
  const { prequalificationStore, authStore } = useContext(RootStoreContext);
  const {
    prequalificationInformation,
    updatePrequalificationInformation,
    intermediaryApplicationId,
    completedSteps,
    prequalificationEntrySource,
  } = prequalificationStore;

  const [isPrimarySalesStaffSectionNeeded, setIsPrimarySalesStaffSectionNeeded] = useState<boolean>(false);

  const [disabledOwnersAmount, setDisabledOwnersAmount] = useState<number>(
    prequalificationInformation?.owners?.length || 0,
  );

  const getOwnerFieldName = (fieldName: OwnersFormInput, index: number | string) => {
    return `${fieldName}_${index}`;
  };

  const allPreviousStepsCompleted = Object.keys(completedSteps!).every((step) => {
    if (step !== PrequalificationStep.Owners) {
      return completedSteps![step as PrequalificationStep] === PrequalificationStepType.Complete;
    }

    return true;
  });

  const defaultValues = {
    [OwnersFormInput.HowManyOwners]: prequalificationInformation?.owners?.length || null,
    ...Object.assign(
      {},
      ...prequalificationInformation?.owners?.map((owner, index) => ({
        [getOwnerFieldName(OwnersFormInput.UserId, index + 1)]: owner.id,
        [getOwnerFieldName(OwnersFormInput.FirstName, index + 1)]: owner.firstName,
        [getOwnerFieldName(OwnersFormInput.LastName, index + 1)]: owner.lastName,
        [getOwnerFieldName(OwnersFormInput.Position, index + 1)]: owner.position,
        [getOwnerFieldName(OwnersFormInput.Email, index + 1)]: owner.email,
        [getOwnerFieldName(OwnersFormInput.IsSalesperson, index + 1)]: '',
        [getOwnerFieldName(OwnersFormInput.CellPhone, index + 1)]: owner.phoneNumber,
        [getOwnerFieldName(OwnersFormInput.PercentageOwned, index + 1)]: owner.percentageOwned,
        [getOwnerFieldName(OwnersFormInput.IsSalesperson, index + 1)]: convertToDefaultQuestionValueFromBoolean(
          owner.isSalesperson,
        ),
        [getOwnerFieldName(OwnersFormInput.IsMainContact, index + 1)]: !!owner.isMainContact,
      }))!,
    ),
  };

  const { errors, values, setValue, validateField, handleSubmit, resetForm } = useForm<FieldValues>({
    defaultValues: defaultValues,
    handleValidation: (fieldName, value) => {
      if (!value) {
        switch (true) {
          case fieldName === OwnersFormInput.HowManyOwners: {
            return validateRequiredField(value);
          }
          case fieldName === OwnersFormInput.PrimarySalesFirstName: {
            return validateRequiredField(value, InputLabel.FirstName);
          }
          case fieldName === OwnersFormInput.PrimarySalesLastName: {
            return validateRequiredField(value, InputLabel.LastName);
          }
          case fieldName === OwnersFormInput.PrimarySalesEmail: {
            return validateRequiredField(value, InputLabel.Email) || isValidEmail(value);
          }
          case fieldName.startsWith(OwnersFormInput.FirstName): {
            return validateRequiredField(value, InputLabel.FirstName);
          }
          case fieldName.startsWith(OwnersFormInput.LastName): {
            return validateRequiredField(value, InputLabel.LastName);
          }
          case fieldName.startsWith(OwnersFormInput.IsSalesperson): {
            return validateRequiredField(value);
          }
          case fieldName.startsWith(OwnersFormInput.Email): {
            return validateRequiredField(value, InputLabel.Email) || isValidEmail(value);
          }
        }
      } else {
        switch (true) {
          case fieldName === OwnersFormInput.PrimarySalesCellPhone: {
            return isValidPhone(value);
          }
          case fieldName === OwnersFormInput.PrimarySalesEmail: {
            return isValidEmail(value);
          }
          case fieldName.startsWith(OwnersFormInput.CellPhone): {
            return isValidPhone(value);
          }
          case fieldName.startsWith(OwnersFormInput.Email): {
            return isValidEmail(value);
          }
          case fieldName.startsWith(OwnersFormInput.PercentageOwned): {
            return isPercentage(value, InputLabel.PercentageOwnedOfCompany) || validatePercentageOwned(value);
          }
        }
      }

      return '';
    },
  });

  const convertTotalOwnersValueToNumber = (totalOwners: string): number => {
    return !!totalOwners ? +totalOwners : 0;
  };

  useEffect(() => {
    const ownersCount = convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]);

    let updatedDefaultValues = {
      [OwnersFormInput.HowManyOwners]: values[OwnersFormInput.HowManyOwners],
    };

    for (let index = 1; index <= ownersCount; index++) {
      updatedDefaultValues = {
        ...updatedDefaultValues,
        [getOwnerFieldName(OwnersFormInput.UserId, index)]:
          values[getOwnerFieldName(OwnersFormInput.UserId, index)] || '',
        [getOwnerFieldName(OwnersFormInput.FirstName, index)]:
          values[getOwnerFieldName(OwnersFormInput.FirstName, index)] || '',
        [getOwnerFieldName(OwnersFormInput.LastName, index)]:
          values[getOwnerFieldName(OwnersFormInput.LastName, index)] || '',
        [getOwnerFieldName(OwnersFormInput.Position, index)]:
          values[getOwnerFieldName(OwnersFormInput.Position, index)] || '',
        [getOwnerFieldName(OwnersFormInput.IsSalesperson, index)]:
          values[getOwnerFieldName(OwnersFormInput.IsSalesperson, index)] || '',
        [getOwnerFieldName(OwnersFormInput.CellPhone, index)]:
          values[getOwnerFieldName(OwnersFormInput.CellPhone, index)] || '',
        [getOwnerFieldName(OwnersFormInput.Email, index)]:
          values[getOwnerFieldName(OwnersFormInput.Email, index)] || '',
        [getOwnerFieldName(OwnersFormInput.PercentageOwned, index)]:
          values[getOwnerFieldName(OwnersFormInput.PercentageOwned, index)] || '',
        [getOwnerFieldName(OwnersFormInput.IsMainContact, index)]:
          values[getOwnerFieldName(OwnersFormInput.IsMainContact, index)] || '',
      };
    }

    resetForm({ ...updatedDefaultValues });
  }, [values[OwnersFormInput.HowManyOwners]]);

  useEffect(() => {
    const ownersCount = convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]);

    const isSalespersonsValues = [];

    for (let index = 1; index <= ownersCount; index++) {
      isSalespersonsValues.push(values[getOwnerFieldName(OwnersFormInput.IsSalesperson, index)]);
    }

    if (
      !!isSalespersonsValues.length &&
      isSalespersonsValues.every((value) => value === DefaultYesNoQuestion.No) &&
      !prequalificationInformation?.isSalespersonExist
    ) {
      setIsPrimarySalesStaffSectionNeeded(true);

      resetForm({
        [OwnersFormInput.PrimarySalesFirstName]: '',
        [OwnersFormInput.PrimarySalesLastName]: '',
        [OwnersFormInput.PrimarySalesPosition]: '',
        [OwnersFormInput.PrimarySalesEmail]: '',
        [OwnersFormInput.PrimarySalesCellPhone]: '',
        ...values,
      });
    } else {
      setIsPrimarySalesStaffSectionNeeded(false);

      resetForm({
        ...Object.fromEntries(
          Object.entries(values).filter(
            ([key]) =>
              ![
                OwnersFormInput.PrimarySalesFirstName,
                OwnersFormInput.PrimarySalesLastName,
                OwnersFormInput.PrimarySalesPosition,
                OwnersFormInput.PrimarySalesEmail,
                OwnersFormInput.PrimarySalesCellPhone,
              ].includes(key as OwnersFormInput),
          ),
        ),
      });
    }
  }, [
    values[getOwnerFieldName(OwnersFormInput.IsSalesperson, 1)],
    values[getOwnerFieldName(OwnersFormInput.IsSalesperson, 2)],
    values[getOwnerFieldName(OwnersFormInput.IsSalesperson, 3)],
    values[getOwnerFieldName(OwnersFormInput.IsSalesperson, 4)],
  ]);

  useEffect(() => {
    const formData = sessionStorage.getItem(OWNERS_FORM_DATA_SESSION_STORAGE_KEY);

    if (formData) {
      resetForm(JSON.parse(formData));

      sessionStorage.removeItem(OWNERS_FORM_DATA_SESSION_STORAGE_KEY);
    }
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;

      setValue(name, value);
    },
    [setValue],
  );

  const handleBlur = useCallback(
    (event: any): void => {
      const name = event.target.name as keyof typeof values;

      validateField(name, values[name]);
    },
    [validateField, values],
  );

  const validateAllEmailsAreDifferent = (formValues: FieldValues): boolean => {
    const emailsForValidation: string[] = [
      formValues[getOwnerFieldName(OwnersFormInput.Email, 1)],
      formValues[getOwnerFieldName(OwnersFormInput.Email, 2)],
      formValues[getOwnerFieldName(OwnersFormInput.Email, 3)],
      formValues[getOwnerFieldName(OwnersFormInput.Email, 4)],
      formValues[OwnersFormInput.PrimarySalesEmail],
    ];

    const filteredEmailsForValidation = emailsForValidation
      .filter((email) => !!email)
      .map((value) => value.trim().toLowerCase());

    return new Set(filteredEmailsForValidation).size === filteredEmailsForValidation.length;
  };

  const handleSaveInformation = handleSubmit(async () => {
    try {
      if (!validateAllEmailsAreDifferent(values)) {
        notify(ToastMessage.EmailsMustBeUniqueForEachOwnerAndSalesperson, 'error');

        return;
      }

      const owners: OwnerInformation[] = [];

      let totalPercentage = 0;

      for (let index = 1; index <= convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]); index++) {
        if (+values[getOwnerFieldName(OwnersFormInput.PercentageOwned, index)]) {
          totalPercentage += +values[getOwnerFieldName(OwnersFormInput.PercentageOwned, index)];
        }
      }

      if (totalPercentage > TOTAL_PERCENTAGE_OWNED_MAX_VALUE) {
        notify(ToastMessage.MaxPercentageOwnedLimitExceeded, 'error');

        return;
      }

      for (let index = 1; index <= convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]); index++) {
        owners.push({
          id: values[getOwnerFieldName(OwnersFormInput.UserId, index)] || '',
          firstName: values[getOwnerFieldName(OwnersFormInput.FirstName, index)],
          lastName: values[getOwnerFieldName(OwnersFormInput.LastName, index)],
          email: values[getOwnerFieldName(OwnersFormInput.Email, index)],
          phoneNumber: values[getOwnerFieldName(OwnersFormInput.CellPhone, index)] || null,
          position: values[getOwnerFieldName(OwnersFormInput.Position, index)] || null,
          percentageOwned: +values[getOwnerFieldName(OwnersFormInput.PercentageOwned, index)] || null,
          isSalesperson: convertToBooleanFromDefaultQuestionValue(
            values[getOwnerFieldName(OwnersFormInput.IsSalesperson, index)],
          ),
        });
      }

      sessionStorage.setItem(OWNERS_FORM_DATA_SESSION_STORAGE_KEY, JSON.stringify(values));

      await updatePrequalificationInformation(intermediaryApplicationId!, {
        owners: owners,
        totalOwners: +values[OwnersFormInput.HowManyOwners],
        stepType: PrequalificationStep.Owners,
        salesperson: isPrimarySalesStaffSectionNeeded
          ? {
              id: null,
              firstName: values[OwnersFormInput.PrimarySalesFirstName],
              lastName: values[OwnersFormInput.PrimarySalesLastName],
              position: values[OwnersFormInput.PrimarySalesPosition] || null,
              phoneNumber: values[OwnersFormInput.PrimarySalesCellPhone] || null,
              email: values[OwnersFormInput.PrimarySalesEmail],
              percentageOwned: null,
            }
          : null,
      });

      sessionStorage.removeItem(OWNERS_FORM_DATA_SESSION_STORAGE_KEY);

      if (prequalificationEntrySource === PrequalificationEntrySource.Registration) {
        navigator(ApplicationRoute.PrequalificationThankYou);
      } else {
        navigator(ApplicationRoute.AccountStatus);
      }
    } catch (error: any) {
      switch (true) {
        case error?.statusCode === CONFLICT_ERROR_STATUS_CODE: {
          notify(ToastMessage.MainContactCannotBeDeleted, 'error');

          break;
        }
        case error?.message === EXISTING_USER_ERROR_MESSAGE: {
          notify(ToastMessage.EmailAlreadyUsedByAnotherUser, 'error');

          break;
        }
        default: {
          notify(ToastMessage.DefaultError, 'error');
        }
      }
    }
  });

  const getInputNames = (index: number): OwnerSection => {
    return {
      id: getOwnerFieldName(OwnersFormInput.UserId, index),
      firstName: getOwnerFieldName(OwnersFormInput.FirstName, index),
      lastName: getOwnerFieldName(OwnersFormInput.LastName, index),
      position: getOwnerFieldName(OwnersFormInput.Position, index),
      isSalesperson: getOwnerFieldName(OwnersFormInput.IsSalesperson, index),
      phone: getOwnerFieldName(OwnersFormInput.CellPhone, index),
      email: getOwnerFieldName(OwnersFormInput.Email, index),
      percentageOwned: getOwnerFieldName(OwnersFormInput.PercentageOwned, index),
      isMainContact: getOwnerFieldName(OwnersFormInput.IsMainContact, index),
    };
  };

  const handleDeleteOwner = (index: number): void => {
    if (values[getOwnerFieldName(OwnersFormInput.IsMainContact, index)]) {
      notify(ToastMessage.MainContactCannotBeDeleted, 'error');

      return;
    }

    const previousTotalOwners = convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]);

    let updatedDefaultValues = values;

    for (let i = index; i < previousTotalOwners; i++) {
      updatedDefaultValues = {
        ...updatedDefaultValues,
        [getOwnerFieldName(OwnersFormInput.UserId, i)]: values[getOwnerFieldName(OwnersFormInput.UserId, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.FirstName, i)]:
          values[getOwnerFieldName(OwnersFormInput.FirstName, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.LastName, i)]:
          values[getOwnerFieldName(OwnersFormInput.LastName, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.Position, i)]:
          values[getOwnerFieldName(OwnersFormInput.Position, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.IsSalesperson, i)]:
          values[getOwnerFieldName(OwnersFormInput.IsSalesperson, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.CellPhone, i)]:
          values[getOwnerFieldName(OwnersFormInput.CellPhone, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.Email, i)]: values[getOwnerFieldName(OwnersFormInput.Email, i + 1)] || '',
        [getOwnerFieldName(OwnersFormInput.PercentageOwned, i)]:
          values[getOwnerFieldName(OwnersFormInput.PercentageOwned, i + 1)] || '',
      };
    }

    updatedDefaultValues = {
      ...updatedDefaultValues,
      [OwnersFormInput.HowManyOwners]: values[OwnersFormInput.HowManyOwners] - 1,
    };

    const keysToRemove: string[] = [
      getOwnerFieldName(OwnersFormInput.UserId, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.FirstName, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.LastName, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.Position, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.IsSalesperson, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.Email, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.CellPhone, previousTotalOwners),
      getOwnerFieldName(OwnersFormInput.PercentageOwned, previousTotalOwners),
    ];

    const filteredValues = Object.fromEntries(
      Object.entries(updatedDefaultValues).filter(([key]) => !keysToRemove.includes(key)),
    );

    resetForm(filteredValues);

    setDisabledOwnersAmount(disabledOwnersAmount - 1);
  };

  const renderOwnersInformation = () => {
    const ownersInformation = [];

    for (let index = 1; index <= convertTotalOwnersValueToNumber(values[OwnersFormInput.HowManyOwners]); index++) {
      const inputNames = getInputNames(index);

      const isAlreadyExistingUser = !!values[inputNames.id];
      const isCurrentUser =
        prequalificationEntrySource === PrequalificationEntrySource.Registration
          ? !!values[inputNames.isMainContact]
          : authStore.account?.email === values[inputNames.email];

      const isDeleteOwnerAllowed = isAlreadyExistingUser && !isCurrentUser;

      ownersInformation.push(
        <div key={getOwnerFieldName(OwnersFormInput.FirstName, index)} className={styles.ownerContainer}>
          <div className={styles.headerWithButton}>
            <div className={styles.mainHeader}>{`Owner ${index}`}</div>
            {isDeleteOwnerAllowed && (
              <div className={styles.deleteIcon} onClick={() => handleDeleteOwner(index)}>
                <DeleteIcon />
                <div className={styles.deleteOwnerText}>Delete</div>
              </div>
            )}
          </div>
          <div className={styles.formInput}>
            <TextInput
              useRequiredSymbol
              labelTitle={InputLabel.FirstName}
              name={inputNames.firstName}
              value={values[inputNames.firstName]}
              errorMessage={errors[inputNames.firstName]}
              className={styles.input}
              containerClassName={styles.inputContainer}
              onChange={handleChange}
            />
            <TextInput
              useRequiredSymbol
              labelTitle={InputLabel.LastName}
              name={inputNames.lastName}
              value={values[inputNames.lastName]}
              errorMessage={errors[inputNames.lastName]}
              className={styles.input}
              containerClassName={styles.inputContainer}
              onChange={handleChange}
            />
          </div>
          <div className={styles.formInput}>
            <InputSelect
              items={OWNER_POSITION_OPTIONS}
              currentValue={values[inputNames.position]}
              onChange={(value) => setValue(inputNames.position, value)}
              label={InputLabel.Position}
              errorMessage={errors[inputNames.position]}
            />
            <InputSelect
              useRequiredSymbol
              items={DEFAULT_YES_NO_QUESTION_OPTIONS}
              currentValue={values[inputNames.isSalesperson]}
              onChange={(value) => setValue(inputNames.isSalesperson, value)}
              label={InputLabel.IsSalesperson}
              errorMessage={errors[inputNames.isSalesperson]}
            />
          </div>
          <div className={styles.formInput}>
            <PhoneNumberInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={InputLabel.Phone}
              onChange={(value: any) => setValue(inputNames.phone, value)}
              data-label={InputLabel.Phone}
              value={values[inputNames.phone]}
              onBlur={handleBlur}
              errorMessage={errors[inputNames.phone]}
            />
            <TextInput
              useRequiredSymbol={!isAlreadyExistingUser}
              labelTitle={InputLabel.Email}
              name={inputNames.email}
              value={values[inputNames.email]}
              errorMessage={errors[inputNames.email]}
              className={styles.input}
              containerClassName={styles.inputContainer}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isAlreadyExistingUser}
              tooltipTitle={isAlreadyExistingUser ? DISABLED_OWNER_EMAIL_TOOLTIP_TITLE : undefined}
            />
          </div>
          <div className={styles.formInput}>
            <TextInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={InputLabel.PercentageOwnedOfCompany}
              name={inputNames.percentageOwned}
              placeholder="%"
              value={removeNonDigits(values[inputNames.percentageOwned])}
              errorMessage={errors[inputNames.percentageOwned]}
              onChange={handleChange}
            />
          </div>
        </div>,
      );
    }

    return ownersInformation;
  };

  const isSubmitDisabled =
    !allPreviousStepsCompleted && prequalificationEntrySource !== PrequalificationEntrySource.Registration;

  return (
    <div className={styles.infoContainer}>
      <div className={styles.mainHeader}>Owners</div>
      <div className={styles.formInput}>
        <InputSelect
          items={HOW_MANY_OWNERS_OPTIONS.map((option) => ({
            ...option,
            disabled: +option.value <= disabledOwnersAmount - 1,
          }))}
          currentValue={values[OwnersFormInput.HowManyOwners]}
          onChange={(value) => setValue(OwnersFormInput.HowManyOwners, value)}
          label={OwnersFormInput.HowManyOwners}
          errorMessage={errors[OwnersFormInput.HowManyOwners]}
          useRequiredSymbol
        />
      </div>
      {renderOwnersInformation()}
      {isPrimarySalesStaffSectionNeeded && (
        <div className={styles.ownerContainer}>
          <div className={styles.mainHeader}>Primary Sales Staff</div>
          <div className={styles.formInput}>
            <TextInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={OwnersFormInput.PrimarySalesFirstName}
              name={OwnersFormInput.PrimarySalesFirstName}
              value={values[OwnersFormInput.PrimarySalesFirstName]}
              errorMessage={errors[OwnersFormInput.PrimarySalesFirstName]}
              onChange={handleChange}
            />
            <TextInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={OwnersFormInput.PrimarySalesLastName}
              name={OwnersFormInput.PrimarySalesLastName}
              value={values[OwnersFormInput.PrimarySalesLastName]}
              errorMessage={errors[OwnersFormInput.PrimarySalesLastName]}
              onChange={handleChange}
            />
          </div>
          <div className={styles.formInput}>
            <InputSelect
              items={OWNER_POSITION_OPTIONS}
              currentValue={values[OwnersFormInput.PrimarySalesPosition]}
              onChange={(value) => setValue(OwnersFormInput.PrimarySalesPosition, value)}
              label={OwnersFormInput.PrimarySalesPosition}
              errorMessage={errors[OwnersFormInput.PrimarySalesPosition]}
            />
          </div>
          <div className={styles.formInput}>
            <PhoneNumberInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={OwnersFormInput.PrimarySalesCellPhone}
              onChange={(value: any) => setValue(OwnersFormInput.PrimarySalesCellPhone, value)}
              data-label={InputLabel.Phone}
              value={values[OwnersFormInput.PrimarySalesCellPhone]}
              onBlur={handleBlur}
              errorMessage={errors[OwnersFormInput.PrimarySalesCellPhone]}
            />
            <TextInput
              useRequiredSymbol
              className={styles.input}
              containerClassName={styles.inputContainer}
              labelTitle={OwnersFormInput.PrimarySalesEmail}
              name={OwnersFormInput.PrimarySalesEmail}
              value={values[OwnersFormInput.PrimarySalesEmail]}
              errorMessage={errors[OwnersFormInput.PrimarySalesEmail]}
              onChange={handleChange}
            />
          </div>
        </div>
      )}
      <div className={styles.buttonsContainer}>
        <Button onClick={handleSaveInformation} disabled={isSubmitDisabled} className={styles.saveButton}>
          Submit
        </Button>
        {isSubmitDisabled && (
          <div className={styles.adviceText}>This form cannot be submitted to HFS until all Steps are completed.</div>
        )}
      </div>
    </div>
  );
};

export default observer(OwnersInformation);
